import { useState, useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import TextAreaField from "~/components/form/text-area";
import { SubmitButton as Button } from "~/components/form/submit-button";
import SearchSelect from "~/components/form/search-select";
import { useBusinessRule } from "~/hooks/businessRule/use-businessRule";

import { BusinessRuleFormValues } from "~/types/business-rule-types";
interface Field {
  id: keyof BusinessRuleFormValues;
  label: string;
  type: string;
  options?: Record<string, string>;
}

interface ExpandableFormProps {
  searchFields: Field[];
  onFormChange: (values: BusinessRuleFormValues) => void;
}

const ExpandableForm = ({
  searchFields,
  onFormChange,
}: ExpandableFormProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { tgeServiceCodes: serviceCodes, businessRuleStatus } =
    useBusinessRule();
  console.log(businessRuleStatus);
  const defaultValues: BusinessRuleFormValues = {
    customerid: "",
    customername: "",
    customerzip: "",
    servicecode: "",
    status: "",
  };

  const methods = useForm<BusinessRuleFormValues>({
    defaultValues,
  });

  const { reset, control } = methods;

  const watchedValues = useWatch({ control });

  useEffect(() => {
    onFormChange(watchedValues as any);
  }, [watchedValues, onFormChange]);

  const handleClear = () => {
    reset(defaultValues);
  };

  const renderField = (field: Field) => {
    switch (field.type) {
      case "text":
        return (
          <TextAreaField
            key={field.id}
            name={field.id}
            label={field.label}
            showLabel
          />
        );

      case "search-select":
        if (field.id === "status") {
          return (
            <SearchSelect
              key={field.id}
              name={field.id}
              label={field.label}
              placeholder={field.label}
              options={businessRuleStatus?.map((status: any) => ({
                key: status.Id,
                value: status.Status,
              }))}
            />
          );
        }

        if (field.id === "servicecode") {
          return (
            <SearchSelect
              key={field.id}
              name={field.id}
              label={field.label}
              placeholder={field.label}
              options={serviceCodes?.map((servicecode: any) => ({
                key: servicecode.servicecode,
                value: `${servicecode.servicecode} - ${servicecode.servicedescription}`,
              }))}
            />
          );
        }

        return (
          <SearchSelect
            key={field.id}
            name={field.id}
            label={field.label}
            placeholder={field.label}
            options={
              field.options
                ? Object.entries(field.options).map(([key, value]) => ({
                    key,
                    value,
                  }))
                : []
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="border rounded shadow p-2 w-full bg-white mx-auto">
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          className="w-full text-left font-semibold text-sm py-2 px-4 bg-gray-100 rounded hover:bg-gray-200"
        >
          {isExpanded ? "Search Business Rules ▲" : "Search Business Rules ▼"}
        </button>

        {isExpanded && (
          <form className="mt-4 space-y-4">
            <div className="grid grid-cols-4 gap-4">
              {searchFields.map(renderField)}
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                size="small"
                variant="secondary"
                type="button"
                onClick={handleClear}
              >
                Clear
              </Button>
            </div>
          </form>
        )}
      </div>
    </FormProvider>
  );
};

export default ExpandableForm;
